
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProfileData } from "./useProfileSettings";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const useProfileUpdates = (profile: ProfileData | null) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  
  const getUtmParams = () => {
    return {
      utm_source: searchParams.get("utm_source") || undefined,
      utm_medium: searchParams.get("utm_medium") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined,
      utm_content: searchParams.get("utm_content") || undefined,
      utm_term: searchParams.get("utm_term") || undefined
    };
  };

  const updateProfile = async (updates: Partial<ProfileData>) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      const utmParams = getUtmParams();
      
      const utmUpdates = {};
      if (profile) {
        Object.entries(utmParams).forEach(([key, value]) => {
          if (value !== undefined && !profile[key as keyof ProfileData] && !updates[key as keyof ProfileData]) {
            utmUpdates[key] = value;
          }
        });
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          ...utmUpdates
        })
        .eq('id', user.id)
        .select();
        
      if (error) throw error;
      
      toast({
        title: t('common.success'),
        description: t('settings.settingsUpdatedSuccess'),
      });
      
      return data?.[0] || null;
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        variant: "destructive",
        title: t('error'),
        description: t('settings.failedToUpdateSettings'),
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    updateProfile
  };
};
